import createFetch from "fetch-retry";
import { P, match } from "ts-pattern";
import { env } from "~/env.mjs";
import { createClient as createInternalMagentoClient } from "~/graphql/genql/magento";
import { createClient as createPimcoreClient } from "~/graphql/genql/pim";
import { config } from "~/i18n/config";

const FgReset = "\x1b[0m";
const FgRed = "\x1b[31m";
const FgGreen = "\x1b[32m";
const FgYellow = "\x1b[33m";
const FgBlueLight = "\x1b[38;5;117m";
const FgBlueDark = "\x1b[38;5;25m";
const FgMagentaLight = "\x1b[38;5;213m";
const FgMagenta = "\x1b[35m";
const FgMagentaDark = "\x1b[38;5;54m";
const FgOrange = "\x1b[38;5;208m";
const FgLightGray = "\x1b[90m";

const debugFetcher = async (input: string | URL | globalThis.Request, init?: RequestInit) => {
	const url = input instanceof Request ? input.url : input.toString();
	const body = typeof init?.body === "string" ? init.body.substring(0, 100) : undefined;

	const createLabel = (props: { color: string; label: string }) => {
		return `${props.color}${props.label}${FgReset}`;
	};

	const method = match(init?.method || "GET")
		.with("GET", (v) => ({ color: FgBlueLight, label: v }))
		.with("POST", (v) => ({ color: FgBlueDark, label: v }))
		.otherwise((v) => ({ color: FgLightGray, label: v }));

	const source = match(url)
		.with(P.string.includes("tps.tosize"), () => ({ color: FgMagentaLight, label: "TPS" }))
		.with(P.string.includes("hemlock.tosize"), () => ({ color: FgMagenta, label: "HEMLOCK" }))
		.with(P.string.includes("api.tosize"), P.string.includes("development.tosize.dev"), () => ({
			color: FgMagentaDark,
			label: "CS",
		}))
		.otherwise(() => ({ color: FgLightGray, label: "UNKNOWN" }));

	const environment = match(url)
		.with(P.string.includes(".local"), () => ({ color: FgGreen, label: "LOCAL" }))
		.with(P.string.includes(".dev"), () => ({ color: FgYellow, label: "DEV" }))
		.with(P.string.includes(".com"), () => ({ color: FgRed, label: "PROD" }))
		.otherwise(() => ({ color: FgLightGray, label: "UNKNOWN" }));

	const labelBase = ` ${createLabel(method)}:${createLabel(environment)}:${createLabel(source)} - ${body ?? ""}...`;

	let startTime = 0;
	if (process.env.NODE_ENV === "development") {
		startTime = performance.now();
	}

	const response = await globalThis.fetch(input, init);

	if (process.env.NODE_ENV === "development") {
		const duration = performance.now() - startTime;
		const durationLabel = `${duration.toFixed(1)}ms`;
		const durationColor = match(duration)
			.with(P.number.gt(1000), () => FgRed)
			.with(P.number.gt(500), () => FgOrange)
			.with(P.number.gt(100), () => FgYellow)
			.otherwise(() => FgGreen);

		console.info(`${labelBase} ${createLabel({ color: durationColor, label: durationLabel })}`);
	}

	return response;
};

const fetcher = createFetch(debugFetcher, {
	retries: 5,
	retryOn: [503, 502],
	retryDelay: 1000,
});

function getApiUrl(locale: string) {
	if (process.env.NODE_ENV === "development" && env.NEXT_PUBLIC_API_URL) {
		return env.NEXT_PUBLIC_API_URL;
	}

	const domain = config.domains.find((domain) => domain.locales.includes(locale));

	if (!domain) throw new Error(`No domain found for locale ${locale}`);

	return domain.api[locale] as string;
}

export const createMagentoClient = (locale: string, headers: HeadersInit = {}, tags?: string[]) => {
	return createInternalMagentoClient({
		url: `${getApiUrl(locale)}/graphql/api`,
		batch: true,
		fetch: fetcher,
		headers: {
			"Content-Type": "application/json",
			"User-Agent": "stulpr-frontend",
			...headers,
		},
		next: { tags },
	});
};

export const pimcore = createPimcoreClient({
	url: env.NEXT_PUBLIC_PIMCORE_API_URL,
	batch: false,
	fetch: fetcher,
});
